/**
 * Geeko cart exit popup — cart page + cart drawer leave interception.
 */
(function () {
  const STORAGE = {
    DAILY_DATE: 'geeko_cart_exit_popup_daily_date',
    DAILY_COUNT: 'geeko_cart_exit_popup_daily_count',
    EXPIRES_AT: 'geeko_cart_exit_popup_expires_at',
    PURCHASED: 'geeko_cart_exit_popup_purchased',
    SECOND_ELIGIBLE: 'geeko_cart_exit_popup_second_eligible',
    DRAWER_SECOND_ELIGIBLE: 'geeko_cart_exit_popup_drawer_second_eligible',
    LEFT_CART: 'geeko_cart_exit_popup_left_cart',
    LEFT_DRAWER: 'geeko_cart_exit_popup_left_drawer',
    FIRST_X: 'geeko_cart_exit_popup_first_x',
    DRAWER_FIRST_X: 'geeko_cart_exit_popup_drawer_first_x',
  };

  function getConfig() {
    return window.geekoCartExitPopupConfig || {};
  }

  function getLocalDateKey() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  function getDailyCount() {
    const today = getLocalDateKey();
    const storedDate = localStorage.getItem(STORAGE.DAILY_DATE);
    if (storedDate !== today) return 0;
    return parseInt(localStorage.getItem(STORAGE.DAILY_COUNT) || '0', 10);
  }

  function recordImpression() {
    const today = getLocalDateKey();
    const count = getDailyCount() + 1;
    localStorage.setItem(STORAGE.DAILY_DATE, today);
    localStorage.setItem(STORAGE.DAILY_COUNT, String(count));
  }

  function isPurchased() {
    return localStorage.getItem(STORAGE.PURCHASED) === '1';
  }

  function getHourMs() {
    const hours = parseFloat(getConfig().countdownHours, 10) || 1;
    return hours * 3600 * 1000;
  }

  class GeekoCartExitPopup extends HTMLElement {
    constructor() {
      super();
      this._isOpen = false;
      this._pending = null;
      this._timerId = null;
      this._historyPushed = false;
      this._ignoreNextPopState = false;
      this._onPopState = this._onPopState.bind(this);
      this._onDocumentClick = this._onDocumentClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onPageHide = this._onPageHide.bind(this);
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      const config = getConfig();
      if (config.enabled === false) return;

      this._cacheDom();
      this._bindUi();
      this._onCartPageEnter();
      this._initCartPageHistory();
      this._observeCartDrawer();
      window.addEventListener('pagehide', this._onPageHide);
      document.addEventListener('click', this._onDocumentClick, true);
      document.addEventListener('keydown', this._onKeydown, true);

      window.GeekoCartExitPopup = this;
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocumentClick, true);
      document.removeEventListener('keydown', this._onKeydown, true);
      window.removeEventListener('popstate', this._onPopState);
      window.removeEventListener('pagehide', this._onPageHide);
      this._stopTimer();
    }

    _isSecondEligible() {
      return localStorage.getItem(STORAGE.SECOND_ELIGIBLE) === '1';
    }

    _markSecondEligible() {
      localStorage.setItem(STORAGE.SECOND_ELIGIBLE, '1');
    }

    _clearSecondEligible() {
      localStorage.removeItem(STORAGE.SECOND_ELIGIBLE);
    }

    _isDrawerSecondEligible() {
      return localStorage.getItem(STORAGE.DRAWER_SECOND_ELIGIBLE) === '1';
    }

    _markDrawerSecondEligible() {
      localStorage.setItem(STORAGE.DRAWER_SECOND_ELIGIBLE, '1');
    }

    _clearDrawerSecondEligible() {
      localStorage.removeItem(STORAGE.DRAWER_SECOND_ELIGIBLE);
    }

    /** 购物车页 / 抽屉：第二次须离开后再进入 */
    _resolveVariant(pending) {
      const count = getDailyCount();
      if (count === 0) return 'first';
      if (count >= 2) return null;
      if (pending?.source === 'page') {
        return this._isSecondEligible() ? 'second' : null;
      }
      if (pending?.source === 'drawer') {
        return this._isDrawerSecondEligible() ? 'second' : null;
      }
      return null;
    }

    _onCartPageEnter() {
      if (!this._isCartPage()) return;
      const count = getDailyCount();
      if (count >= 1 && sessionStorage.getItem(STORAGE.LEFT_CART) === '1') {
        this._markSecondEligible();
        sessionStorage.removeItem(STORAGE.LEFT_CART);
        sessionStorage.removeItem(STORAGE.FIRST_X);
      }
    }

    _onPageHide() {
      if (!this._isCartPage()) return;
      const nav = performance.getEntriesByType('navigation')[0];
      if (nav?.type === 'reload') return;
      sessionStorage.setItem(STORAGE.LEFT_CART, '1');
    }

    _observeCartDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || drawer._geekoExitObserved) return;
      drawer._geekoExitObserved = true;

      let wasActive = drawer.classList.contains('active');

      const onDrawerOpen = () => {
        const count = getDailyCount();
        if (count >= 1 && sessionStorage.getItem(STORAGE.LEFT_DRAWER) === '1') {
          this._markDrawerSecondEligible();
          sessionStorage.removeItem(STORAGE.LEFT_DRAWER);
          sessionStorage.removeItem(STORAGE.DRAWER_FIRST_X);
        }
      };

      const observer = new MutationObserver(() => {
        const active = drawer.classList.contains('active');
        if (!wasActive && active) {
          onDrawerOpen();
        }
        if (wasActive && !active) {
          sessionStorage.setItem(STORAGE.LEFT_DRAWER, '1');
        }
        wasActive = active;
      });

      observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      if (wasActive) onDrawerOpen();
    }

    _cacheDom() {
      this._backdrop = this.querySelector('.geeko-cart-exit-popup__backdrop');
      this._panelFirst = this.querySelector('[data-variant="first"]');
      this._panelSecond = this.querySelector('[data-variant="second"]');
    }

    _bindUi() {
      this.querySelectorAll('[data-geeko-exit-close]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this._onCloseX();
        });
      });
      this.querySelectorAll('[data-geeko-exit-continue]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this._onContinue();
        });
      });
      this.querySelectorAll('[data-geeko-exit-checkout]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this._onCheckout();
        });
      });
    }

    _getActivePanel() {
      if (!this._panelFirst?.hidden) return this._panelFirst;
      if (!this._panelSecond?.hidden) return this._panelSecond;
      return null;
    }

    _getTimerEls(panel) {
      const root = panel || this._getActivePanel();
      if (!root) return null;
      return {
        hours: root.querySelector('[data-geeko-exit-hours]'),
        minutes: root.querySelector('[data-geeko-exit-minutes]'),
        seconds: root.querySelector('[data-geeko-exit-seconds]'),
      };
    }

    _ensureExpiresAt() {
      const hourMs = getHourMs();
      let expires = parseInt(localStorage.getItem(STORAGE.EXPIRES_AT) || '0', 10);
      if (!expires || Date.now() >= expires) {
        expires = Date.now() + hourMs;
        localStorage.setItem(STORAGE.EXPIRES_AT, String(expires));
      }
      return expires;
    }

    _restartCountdown() {
      const expires = Date.now() + getHourMs();
      localStorage.setItem(STORAGE.EXPIRES_AT, String(expires));
      return expires;
    }

    _renderTimer(panel) {
      const els = this._getTimerEls(panel);
      if (!els?.hours) return;

      let remaining = Math.max(0, this._ensureExpiresAt() - Date.now());
      if (remaining === 0) {
        this._restartCountdown();
        remaining = getHourMs();
      }

      const totalSec = Math.floor(remaining / 1000);
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      const pad = (n) => String(n).padStart(2, '0');

      els.hours.textContent = pad(h);
      els.minutes.textContent = pad(m);
      els.seconds.textContent = pad(s);
    }

    _startTimer() {
      this._stopTimer();
      const tick = () => {
        const panel = this._getActivePanel();
        if (!panel) return;
        const remaining = Math.max(0, parseInt(localStorage.getItem(STORAGE.EXPIRES_AT) || '0', 10) - Date.now());
        if (remaining === 0) {
          this._restartCountdown();
        }
        this._renderTimer(panel);
      };
      tick();
      this._timerId = window.setInterval(tick, 1000);
    }

    _stopTimer() {
      if (this._timerId) {
        clearInterval(this._timerId);
        this._timerId = null;
      }
    }

    _isCartPage() {
      return (
        document.body.classList.contains('page-cart') ||
        (typeof Shopify !== 'undefined' && Shopify.template?.name === 'cart')
      );
    }

    _normalizePath(href) {
      try {
        return new URL(href, window.location.origin).pathname.replace(/\/$/, '') || '/';
      } catch {
        return '';
      }
    }

    _isCartUrl(href) {
      const config = getConfig();
      const cartPath = this._normalizePath(config.cartUrl || '/cart');
      return this._normalizePath(href) === cartPath;
    }

    _isCheckoutTarget(el) {
      return !!el?.closest(
        '#checkout, #CartDrawer-Checkout, [name="checkout"], .cart__checkout-button, [data-geeko-exit-checkout]'
      );
    }

    async _fetchCartEmpty() {
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!res.ok) return true;
        const cart = await res.json();
        return !cart.item_count;
      } catch {
        return true;
      }
    }

    _canShowBase() {
      const config = getConfig();
      if (config.enabled === false) return false;
      if (isPurchased()) return false;
      if (getDailyCount() >= 2) return false;
      return true;
    }

    _isEmptyFromDom(pending) {
      if (pending?.type === 'drawer-close' && pending.drawer) {
        const drawer = pending.drawer;
        if (drawer.classList.contains('is-empty')) return true;
        if (drawer.querySelector('cart-drawer-items.is-empty')) return true;
        return false;
      }
      if (document.querySelector('cart-items.is-empty')) return true;
      if (document.querySelector('cart-drawer.is-empty.active')) return true;
      return false;
    }

    async _canShow(pending) {
      if (!this._canShowBase()) return false;
      if (pending && this._isEmptyFromDom(pending)) return false;
      if (await this._fetchCartEmpty()) return false;
      return true;
    }

    _tryShowSync(pending) {
      if (this._isOpen) return false;
      if (!this._canShowBase()) return false;
      if (this._isEmptyFromDom(pending)) return false;

      const variant = this._resolveVariant(pending);
      if (!variant) return false;

      this._pending = pending;
      this._show(variant);
      recordImpression();
      if (variant === 'second') this._clearSecondEligibleFor(pending?.source);
      this._publish('geeko_cart_exit_popup_impression', { variant, source: pending?.source });
      return true;
    }

    async _tryShow(pending) {
      if (this._isOpen) return false;
      if (!(await this._canShow(pending))) return false;

      const variant = this._resolveVariant(pending);
      if (!variant) return false;

      this._pending = pending;
      this._show(variant);
      recordImpression();
      if (variant === 'second') this._clearSecondEligibleFor(pending?.source);
      this._publish('geeko_cart_exit_popup_impression', { variant, source: pending?.source });
      return true;
    }

    _clearSecondEligibleFor(source) {
      if (source === 'page') this._clearSecondEligible();
      if (source === 'drawer') this._clearDrawerSecondEligible();
    }

    _show(variant) {
      this._isOpen = true;
      this.removeAttribute('hidden');
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');

      if (this._panelFirst) this._panelFirst.hidden = variant !== 'first';
      if (this._panelSecond) this._panelSecond.hidden = variant !== 'second';

      this._ensureExpiresAt();
      this._startTimer();

      document.body.classList.add('overflow-hidden', 'geeko-cart-exit-popup-open');

      // const panel = this._getActivePanel();
      // const focusTarget = panel?.querySelector('.geeko-cart-exit-popup__checkout');
      // if (typeof trapFocus === 'function' && panel) {
      //   trapFocus(this, focusTarget || panel);
      // } else {
      //   focusTarget?.focus();
      // }
    }

    _hide() {
      this._isOpen = false;
      this.setAttribute('hidden', '');
      this.setAttribute('aria-hidden', 'true');
      this.classList.remove('is-open');
      if (this._panelFirst) this._panelFirst.hidden = true;
      if (this._panelSecond) this._panelSecond.hidden = true;
      this._stopTimer();

      const drawerOpen = document.querySelector('cart-drawer.active');
      if (!drawerOpen) {
        document.body.classList.remove('overflow-hidden', 'geeko-cart-exit-popup-open');
      }

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this._opener);
      }
      this._opener = null;
    }

    _onCloseX() {
      const pending = this._pending;
      const variant = !this._panelSecond?.hidden ? 'second' : 'first';
      this._publish('geeko_cart_exit_popup_close', { variant });
      this._pending = null;
      this._hide();
      if (variant === 'first' && pending?.type === 'back') {
        sessionStorage.setItem(STORAGE.FIRST_X, '1');
        this._restoreCartHistoryTrap();
      }
      if (variant === 'first' && pending?.type === 'drawer-close') {
        sessionStorage.setItem(STORAGE.DRAWER_FIRST_X, '1');
      }
    }

    _onContinue() {
      const pending = this._pending;
      const variant = !this._panelSecond?.hidden ? 'second' : 'first';
      this._publish('geeko_cart_exit_popup_continue', {
        variant,
        source: pending?.source,
      });
      this._hide();
      this._pending = null;
      this._runPending(pending);
    }

    _onCheckout() {
      const variant = !this._panelSecond?.hidden ? 'second' : 'first';
      this._publish('geeko_cart_exit_popup_checkout', { variant });
      this._pending = null;
      this._hide();

      const checkoutBtn = document.querySelector(
        '#checkout:not([disabled]), #CartDrawer-Checkout:not([disabled]), .cart__checkout-button[name="checkout"]:not([disabled])'
      );
      if (checkoutBtn) {
        checkoutBtn.click();
        return;
      }
      const form = document.querySelector('#cart, #CartDrawer-Form');
      if (form) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'checkout';
        form.appendChild(input);
        form.submit();
        return;
      }
      window.location.href = getConfig().checkoutUrl || '/checkout';
    }

    _runPending(pending) {
      if (!pending) return;

      if (pending.type === 'back') {
        this._ignoreNextPopState = true;
        history.back();
        return;
      }
      if (pending.type === 'href') {
        window.location.href = pending.url;
        return;
      }
      if (pending.type === 'drawer-close' && pending.drawer) {
        pending.drawer.close();
      }
    }

    _restoreCartHistoryTrap() {
      if (!this._isCartPage()) return;
      history.pushState({ geekoCartExit: true }, '', window.location.href);
    }

    _initCartPageHistory() {
      if (!this._isCartPage()) return;
      if (!this._historyPushed) {
        this._restoreCartHistoryTrap();
        this._historyPushed = true;
      }
      window.addEventListener('popstate', this._onPopState);
    }

    _onPopState() {
      if (this._ignoreNextPopState) {
        this._ignoreNextPopState = false;
        return;
      }
      if (!this._isCartPage() || this._isOpen) return;

      const pending = { type: 'back', source: 'page' };
      const variant = this._resolveVariant(pending);
      if (!variant) {
        this._ignoreNextPopState = true;
        history.back();
        return;
      }
      this._tryShow(pending);
    }

    _onDocumentClick(event) {
      if (this._isOpen) return;

      const drawer = document.querySelector('cart-drawer');
      if (!drawer?.classList.contains('active')) return;

      // 抽屉：仅点击 ×（.drawer__close）关闭时弹窗
      const closeBtn = event.target.closest('cart-drawer .drawer__close');
      if (!closeBtn) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const pending = { type: 'drawer-close', drawer, source: 'drawer' };
      if (this._tryShowSync(pending)) return;
      drawer.close();
    }

    _onKeydown(event) {
      if (!this._isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this._onCloseX();
      }
    }

    _publish(name, payload) {
      if (typeof Shopify !== 'undefined' && Shopify.analytics?.publish) {
        Shopify.analytics.publish(name, payload);
      }
    }
  }

  if (!customElements.get('geeko-cart-exit-popup')) {
    customElements.define('geeko-cart-exit-popup', GeekoCartExitPopup);
  }

  function boot() {
    const el = document.querySelector('geeko-cart-exit-popup');
    if (el && !el._initialized) {
      /* connectedCallback runs automatically */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
